// ===========================================================================
// Control Panel Component
// Sliders and buttons for simulation and pipeline parameters
// ===========================================================================

import React from 'react';
import {
  PipelineThresholds,
  SimulationParams,
  DEFAULT_THRESHOLDS,
  DEFAULT_SIM_PARAMS,
  UIState,
} from '../types';
import './ControlPanel.css';

interface ControlPanelProps {
  thresholds: PipelineThresholds;
  simParams: SimulationParams;
  uiState: UIState;
  running: boolean;
  onThresholdsChange: (thresholds: PipelineThresholds) => void;
  onSimParamsChange: (params: SimulationParams) => void;
  onUIStateChange: (state: UIState) => void;
  onSpawnCrowd: () => void;
  onSpawnLine: () => void;
  onSpawnRandom: () => void;
  onAddWeaponToNearest: () => void;
  onReset: () => void;
  onToggleRunning: () => void;
  onStep: () => void;
}

export const ControlPanel: React.FC<ControlPanelProps> = ({
  thresholds,
  simParams,
  uiState,
  running,
  onThresholdsChange,
  onSimParamsChange,
  onUIStateChange,
  onSpawnCrowd,
  onSpawnLine,
  onSpawnRandom,
  onAddWeaponToNearest,
  onReset,
  onToggleRunning,
  onStep,
}) => {
  const updateThreshold = <K extends keyof PipelineThresholds>(
    key: K,
    value: PipelineThresholds[K]
  ) => {
    onThresholdsChange({ ...thresholds, [key]: value });
  };

  const updateSimParam = <K extends keyof SimulationParams>(
    key: K,
    value: SimulationParams[K]
  ) => {
    onSimParamsChange({ ...simParams, [key]: value });
  };

  const updateUIState = <K extends keyof UIState>(
    key: K,
    value: UIState[K]
  ) => {
    onUIStateChange({ ...uiState, [key]: value });
  };

  return (
    <div className="control-panel">
      {/* Simulation Controls */}
      <section className="control-section">
        <h3 className="section-title">
          <span className="icon">▶</span> Simulation
        </h3>
        
        <div className="button-group">
          <button 
            className={`btn ${running ? 'btn-danger' : 'btn-success'}`}
            onClick={onToggleRunning}
          >
            {running ? '⏸ Pause' : '▶ Run'}
          </button>
          <button 
            className="btn btn-secondary"
            onClick={onStep}
            disabled={running}
          >
            ⏭ Step
          </button>
          <button 
            className="btn btn-warning"
            onClick={onReset}
          >
            🔄 Reset
          </button>
        </div>

        <div className="button-group">
          <button className="btn btn-primary" onClick={onSpawnCrowd}>
            👥 Spawn Crowd
          </button>
          <button className="btn btn-secondary" onClick={onSpawnLine}>
            ➖ Spawn Line
          </button>
          <button className="btn btn-secondary" onClick={onSpawnRandom}>
            🎲 Random
          </button>
        </div>

        <button 
          className="btn btn-warning w-full"
          onClick={onAddWeaponToNearest}
        >
          🔫 Add Weapon to Nearest
        </button>
      </section>

      {/* Display Options */}
      <section className="control-section">
        <h3 className="section-title">
          <span className="icon">👁</span> Display
        </h3>
        
        <div className="checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={uiState.showBoundingBoxes}
              onChange={(e) => updateUIState('showBoundingBoxes', e.target.checked)}
            />
            Bounding Boxes
          </label>
          <label>
            <input
              type="checkbox"
              checked={uiState.showTrackIds}
              onChange={(e) => updateUIState('showTrackIds', e.target.checked)}
            />
            Track IDs
          </label>
          <label>
            <input
              type="checkbox"
              checked={uiState.showTrackHistory}
              onChange={(e) => updateUIState('showTrackHistory', e.target.checked)}
            />
            Track History
          </label>
          <label>
            <input
              type="checkbox"
              checked={uiState.showContours}
              onChange={(e) => updateUIState('showContours', e.target.checked)}
            />
            Crowd Contours
          </label>
          <label>
            <input
              type="checkbox"
              checked={uiState.showDepthOverlay}
              onChange={(e) => updateUIState('showDepthOverlay', e.target.checked)}
            />
            Depth Overlay
          </label>
          <label>
            <input
              type="checkbox"
              checked={uiState.showHeatmapOverlay}
              onChange={(e) => updateUIState('showHeatmapOverlay', e.target.checked)}
            />
            Heatmap Overlay
          </label>
        </div>
      </section>

      {/* Crowd Detection Thresholds */}
      <section className="control-section">
        <h3 className="section-title">
          <span className="icon">👥</span> Crowd Detection
        </h3>
        
        <Slider
          label="Crowd Threshold"
          value={thresholds.crowdThreshold}
          min={1}
          max={20}
          step={1}
          onChange={(v) => updateThreshold('crowdThreshold', v)}
        />
        <Slider
          label="Min Group Size"
          value={thresholds.minGroupSize}
          min={2}
          max={10}
          step={1}
          onChange={(v) => updateThreshold('minGroupSize', v)}
        />
        <Slider
          label="Spatial Distance"
          value={thresholds.spatialDistanceThreshold}
          min={30}
          max={200}
          step={5}
          onChange={(v) => updateThreshold('spatialDistanceThreshold', v)}
          unit="px"
        />
        <Slider
          label="Depth Threshold"
          value={thresholds.depthThreshold}
          min={0.05}
          max={0.5}
          step={0.01}
          onChange={(v) => updateThreshold('depthThreshold', v)}
        />
      </section>

      {/* Heatmap Settings */}
      <section className="control-section">
        <h3 className="section-title">
          <span className="icon">🔥</span> Heatmap
        </h3>
        
        <Slider
          label="Heatmap Sigma"
          value={thresholds.heatmapSigma}
          min={0.5}
          max={5}
          step={0.1}
          onChange={(v) => updateThreshold('heatmapSigma', v)}
        />
        <Slider
          label="Heatmap Threshold"
          value={thresholds.heatmapThreshold}
          min={0.1}
          max={0.9}
          step={0.05}
          onChange={(v) => updateThreshold('heatmapThreshold', v)}
        />
        <Slider
          label="Morph Kernel"
          value={thresholds.morphKernelSize}
          min={1}
          max={7}
          step={2}
          onChange={(v) => updateThreshold('morphKernelSize', v)}
        />
        <Slider
          label="History Length"
          value={thresholds.historyLength}
          min={1}
          max={15}
          step={1}
          onChange={(v) => updateThreshold('historyLength', v)}
          unit=" frames"
        />
      </section>

      {/* Suspect Detection */}
      <section className="control-section">
        <h3 className="section-title">
          <span className="icon">🎯</span> Suspect Detection
        </h3>
        
        <Slider
          label="Proximity Distance"
          value={thresholds.suspectProximity}
          min={30}
          max={150}
          step={5}
          onChange={(v) => updateThreshold('suspectProximity', v)}
          unit="px"
        />
        <Slider
          label="Confirm Frames"
          value={thresholds.suspectConfirmFrames}
          min={1}
          max={30}
          step={1}
          onChange={(v) => updateThreshold('suspectConfirmFrames', v)}
        />
        <Slider
          label="Pin Frames"
          value={thresholds.suspectPinFrames}
          min={1}
          max={60}
          step={1}
          onChange={(v) => updateThreshold('suspectPinFrames', v)}
        />
      </section>

      {/* Simulation Parameters */}
      <section className="control-section">
        <h3 className="section-title">
          <span className="icon">⚙</span> Simulation
        </h3>
        
        <Slider
          label="Detection Noise"
          value={simParams.detectionNoise}
          min={0}
          max={0.2}
          step={0.01}
          onChange={(v) => updateSimParam('detectionNoise', v)}
        />
        <Slider
          label="Dropout Rate"
          value={simParams.dropoutRate}
          min={0}
          max={0.3}
          step={0.01}
          onChange={(v) => updateSimParam('dropoutRate', v)}
        />
        <Slider
          label="False Positive Rate"
          value={simParams.falsePositiveRate}
          min={0}
          max={0.1}
          step={0.005}
          onChange={(v) => updateSimParam('falsePositiveRate', v)}
        />
        <Slider
          label="Agent Speed"
          value={simParams.agentSpeed}
          min={0}
          max={3}
          step={0.1}
          onChange={(v) => updateSimParam('agentSpeed', v)}
        />
        <Slider
          label="Depth Noise"
          value={simParams.depthNoise}
          min={0}
          max={0.1}
          step={0.005}
          onChange={(v) => updateSimParam('depthNoise', v)}
        />

        <div className="select-group">
          <label>Depth Mode</label>
          <select
            value={simParams.depthMode}
            onChange={(e) => updateSimParam('depthMode', e.target.value as 'perspective-y' | 'radial')}
          >
            <option value="perspective-y">Perspective Y-depth</option>
            <option value="radial">Radial depth</option>
          </select>
        </div>
      </section>

      {/* Reset to Defaults */}
      <section className="control-section">
        <button
          className="btn btn-secondary w-full"
          onClick={() => {
            onThresholdsChange(DEFAULT_THRESHOLDS);
            onSimParamsChange(DEFAULT_SIM_PARAMS);
          }}
        >
          ↩ Reset All to Defaults
        </button>
      </section>
    </div>
  );
};

// Slider Component
interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  unit?: string;
}

const Slider: React.FC<SliderProps> = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
  unit = '',
}) => {
  return (
    <div className="slider-group">
      <div className="slider-label">
        <span>{label}</span>
        <span>
          {typeof value === 'number' && value % 1 !== 0
            ? value.toFixed(2)
            : value}
          {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
    </div>
  );
};
